import React from 'react';
import { cn } from '../../utils/cn';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  className = '',
}) => {
  const switchSizes = {
    sm: { track: 'w-7 h-4', thumb: 'w-3 h-3', translate: 'translate-x-3' },
    md: { track: 'w-9 h-5', thumb: 'w-4 h-4', translate: 'translate-x-4' },
  };

  const currentSize = switchSizes[size];

  return (
    <div className={cn('flex items-start justify-between gap-3 select-none', className)}>
      {(label || description) && (
        <div className="flex flex-col">
          {label && (
            <span
              onClick={() => !disabled && onChange(!checked)}
              className={cn(
                'text-xs font-medium text-cf-textPrimary',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              )}
            >
              {label}
            </span>
          )}
          {description && (
            <span className="text-[11px] text-cf-textMuted mt-0.5">{description}</span>
          )}
        </div>
      )}

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cf-orange/50 disabled:opacity-50 disabled:cursor-not-allowed',
          currentSize.track,
          checked ? 'bg-cf-orange' : 'bg-cf-border'
        )}
      >
        <span
          className={`pointer-events-none inline-block transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
            currentSize.thumb
          } ${checked ? currentSize.translate : 'translate-x-0'}`}
        />
      </button>
    </div>
  );
};
